    import {forwardRef, Inject, Pipe, PipeTransform} from '@angular/core';
import {SettingsService} from "@app/core";
import * as moment from "moment";

/** Parse PHP JSON `date` field without `Date(string)` (WebKit treats unzoned ISO as UTC). */
export function localDateFromPhpDateField(dateStr: string): Date | null {
    if (typeof dateStr !== 'string') {
        return null;
    }
    const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);
    if (!m) {
        return null;
    }
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
}

const isoInstantRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/;

/** Symfony JsonSerializable DateTime `{ date, timezone }` — respect UTC vs local wall (see timeline-date pipe). */
export function dateFromPhpDateTimeObject(value: { date?: string; timezone?: string }): Date | null {
    if (!value || typeof value.date !== 'string') {
        return null;
    }
    const dateStr = value.date;
    const normalized = dateStr.indexOf(' ') !== -1 ? dateStr.replace(' ', 'T') : dateStr;
    if (isoInstantRegex.test(normalized)) {
        const d = new Date(normalized);
        return isNaN(d.getTime()) ? null : d;
    }
    const asUtcZ = normalized.replace(/\.\d+$/, '') + 'Z';
    const phpTz = value.timezone != null ? String(value.timezone) : '';
    const u = phpTz.toUpperCase();
    const isPhpUtc = u === 'UTC' || u === 'ETC/UTC' || u === 'ETC/GMT';
    if (isPhpUtc && isoInstantRegex.test(asUtcZ)) {
        const d = new Date(asUtcZ);
        return isNaN(d.getTime()) ? null : d;
    }
    return localDateFromPhpDateField(dateStr);
}

@Pipe({
  name: 'time'
})
export class TimePipe implements PipeTransform {
    format: string = '';

    constructor(@Inject(forwardRef(() => SettingsService)) private settings) {
    }

    transform(value: any, args?: any): any {
        if(value=='' || value==null){
            return '';
        }
        let language = this.settings._csLanguage;
        if(language == 'en'){
            language = 'en-GB';
        }
        moment.locale(language);
        const intlSettings = this.settings._csIntlSettings;

        if (intlSettings['time'] == 1) {
            this.format = 'HH:mm';
        } else {
            this.format = 'hh:mm a';
        }

        let momentInput: Date;

        if (value instanceof Date) {
            momentInput = value;
        } else if (value && typeof value === 'object' && value.hasOwnProperty('date')) {
            const parsed = dateFromPhpDateTimeObject(value);
            if (!parsed) {
                return '';
            }
            momentInput = parsed;
        } else {
            const valStr = typeof value === 'string' ? value : String(value);
            const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/;
            let utcTimeString = (isoRegex.test(valStr)) ? new Date(valStr) : new Date(valStr + ' UTC');
            utcTimeString = new Date(utcTimeString.toLocaleString(this.settings._csLanguage, { timeZone: this.settings._csCountryCode.timezone }));
            momentInput = utcTimeString;
        }

        return moment(momentInput).format(this.format);
  }

}
